import axios from 'axios';
import {
  EVENT_SCORES_REQUEST,
  EVENT_SCORES_SUCCESS,
  EVENT_SCORES_FAILURE
} from 'Store/constants';
import {
  EVENT_FIELD_MAPPING,
  FOOTBALL,
  BASKETBALL,
  TENNIS,
  VOLLEYBALL,
  HANDBALL,
  MOTORSPORTS,
  SNOOKER,
  PINGPONG,
  ICE_HOCKEY,
  AMERICAN_FOOTBALL,
  MMA,
  E_FUTBOL,
  E_BASKETBOL
} from 'Store/constants/sportTypes';
import ProjectConfig from 'ProjectConfig';
import EnvironmentManager from 'Utils/environmentManager';

const API_GATEWAY = ProjectConfig.API_GATEWAY;

let eventScoresInFlight = new Map();
let eventScoresCache = new Map();
const CACHE_DURATION = 10000; // 10 seconds cache

const SPORT_TYPE_STRING_TO_NUMBER = {
  FOOTBALL: FOOTBALL,
  SOCCER: FOOTBALL,
  BASKETBALL: BASKETBALL,
  TENNIS: TENNIS,
  VOLLEYBALL: VOLLEYBALL,
  HANDBALL: HANDBALL,
  MOTORSPORTS: MOTORSPORTS,
  SNOOKER: SNOOKER,
  PINGPONG: PINGPONG,
  ICE_HOCKEY: ICE_HOCKEY,
  AMERICAN_FOOTBALL: AMERICAN_FOOTBALL,
  MMA: MMA,
  E_FUTBOL: E_FUTBOL,
  E_BASKETBOL: E_BASKETBOL,
  Futbol: FOOTBALL,
  Basketbol: BASKETBALL,
  Tenis: TENNIS,
  Voleybol: VOLLEYBALL,
  Hentbol: HANDBALL,
  'Motor Sporları': MOTORSPORTS,
  Snooker: SNOOKER,
  'Masa Tenisi': PINGPONG,
  'Buz Hokeyi': ICE_HOCKEY,
  'Amerikan Futbolu': AMERICAN_FOOTBALL,
  'E-Futbol': E_FUTBOL,
  'E-Basketbol': E_BASKETBOL
};

const createEventListKey = eventList => {
  if (!eventList || !Array.isArray(eventList)) return 'empty';
  
  const hasSelectionsArrays = eventList?.filter(event => event.selections);
  const eventListParams = hasSelectionsArrays.length > 0
    ? scoreModelHandler(eventList)
    : scoreModelHandler([{ selections: eventList }]);
  
  return JSON.stringify(eventListParams);
};

const isCacheValid = cacheEntry => {
  return cacheEntry && (Date.now() - cacheEntry.timestamp) < CACHE_DURATION;
};

export const getEventScores = eventList => async dispatch => {
  if (typeof eventList === 'undefined') return;
  
  const eventListKey = createEventListKey(eventList);
  
  const cachedResult = eventScoresCache.get(eventListKey);
  if (isCacheValid(cachedResult)) {
    dispatch({
      type: EVENT_SCORES_SUCCESS,
      payload: cachedResult.data
    });
    return cachedResult.response;
  }
  
  // De-dupe concurrent requests with same eventList
  if (eventScoresInFlight.has(eventListKey)) {
    return eventScoresInFlight.get(eventListKey);
  }
  
  const hasSelectionsArrays = eventList?.filter(event => event.selections);
  const eventListParams = hasSelectionsArrays.length > 0
    ? scoreModelHandler(eventList)
    : scoreModelHandler([{ selections: eventList }]);

  try {
    dispatch({
      type: EVENT_SCORES_REQUEST
    });

    const isGetMethodEnabled = EnvironmentManager.getMessageText({ key: 'live_score_get_method_enabled' }) === 'true';

    let url, method, data;

    if (isGetMethodEnabled) {
      url = `${API_GATEWAY}/mobile/live-score/event/v2/sport-list?${eventListParams}`;
      method = 'GET';
      data = undefined;
    } else {
      const scoreList = {
        sports: hasSelectionsArrays.length > 0
          ? scoreModelHandler(eventList)
          : scoreModelHandler([{ selections: eventList }])
      };
      url = `${API_GATEWAY}/mobile/live-score/event/sport-list`;
      method = 'POST';
      data = scoreList;
    }

    const request = axios({
      url,
      method,
      data
    }).finally(() => {
      eventScoresInFlight.delete(eventListKey);
    });
    
    eventScoresInFlight.set(eventListKey, request);
    const res = await request;

    // Cache the result
    eventScoresCache.set(eventListKey, {
      data: res.data.events,
      response: res,
      timestamp: Date.now()
    });

    dispatch({
      type: EVENT_SCORES_SUCCESS,
      payload: res.data.events
    });
  } catch (err) {
    eventScoresInFlight.delete(eventListKey);
    dispatch({
      type: EVENT_SCORES_FAILURE,
      payload: err
    });
  }
};

const scoreModelHandler = data => {
  const sportsMap = new Map();
  
  for (let coupon of data) {
    if (!coupon || !coupon.selections) {
      continue;
    }
    
    for (let event of coupon.selections) {
      if (!event) {
        continue;
      }
      
      const eventId = event[EVENT_FIELD_MAPPING.eventId]
                     || (typeof event.id === 'number' ? event.id : event.eventId);
      let sportType = event[EVENT_FIELD_MAPPING.sportType]
                     || event.sportType
                     || event.sportId;

      if (!eventId || (!sportType && sportType !== 0)) {
        console.warn('Skipping event with undefined eventId or sportType:', {
          eventId,
          sportType,
          event: {
            id: event.id,
            eventId: event.eventId,
            sportType: event.sportType,
            sportId: event.sportId
          }
        });
        continue;
      }

      if (typeof sportType === 'string') {
        if (SPORT_TYPE_STRING_TO_NUMBER[sportType]) {
          sportType = SPORT_TYPE_STRING_TO_NUMBER[sportType];
        } else {
          const convertedSportType = Number(sportType);
          if (!isNaN(convertedSportType)) {
            sportType = convertedSportType;
          } else {
            console.warn('Could not convert sportType to number:', sportType);
            sportType = FOOTBALL;
          }
        }
      }

      if (!sportType && sportType !== 0) {
        sportType = FOOTBALL;
      }

      if (!sportsMap.has(sportType)) {
        sportsMap.set(sportType, []);
      }

      const existingEventIds = sportsMap.get(sportType);
      if (!existingEventIds.includes(eventId)) {
        existingEventIds.push(eventId);
      }
    }
  }
  
  const isGetMethodEnabled = EnvironmentManager.getMessageText({ key: 'live_score_get_method_enabled' }) === 'true';

  if (isGetMethodEnabled) {
    const eventListParams = [];
    for (const [sportType, eventIds] of sportsMap) {
      const param = `eventList=${sportType}:${eventIds.join(';')}`;
      eventListParams.push(param);
    }
    return eventListParams.join('&');
  } else {
    const sports = [];
    for (const [sportType, eventIds] of sportsMap) {
      sports.push({
        sbsEventIds: eventIds,
        sportType: sportType
      });
    }
    return sports;
  }
};
